/**
 * The stock-display ruleset, derived once for every surface that consumes
 * whs/get_inventory_and_pricing: browse cards (partials/product-browse.js), the quick
 * order modal and multi-order table (ordering/inventory.js) and the checkout rows
 * (partials/checkout-inventory.js).
 *
 * The endpoint answers in raw numbers and decides nothing, so every consumer used to
 * carry its own copy of the stock-state ladder and the four copies had drifted - the
 * same part could read "3 Available" on a browse card and "Out of Stock" at checkout.
 * Everything here is pure: no jQuery, no DOM, no module state. A surface passes what it
 * knows (response item, brand settings, site flags, wordings) and renders the returned
 * view model however its markup wants.
 *
 * Issue #301. Behaviour decisions made when converging are marked DECISION below.
 */

/**
 * The warehouse matches DECODED part numbers, and a part number can reach these surfaces
 * already percent-encoded - through a URL segment or a JSON payload built from one - so
 * the decode has to happen somewhere before the batch goes out.
 *
 * Note what that does NOT mean: two of the four request sites read a value that was never
 * encoded (product-browse.js reads data-pn, which _product_card.inc emits as the raw
 * $item->PartNumber, and getMultiItemInventory() reads the buyer's live keystrokes), so
 * for them this is a transformation rather than a round trip. A literal part number
 * carrying a '%' and two hex digits after it - "A%2B1" - is changed by it. That was
 * already true of the per-row path before #301; applying it at all four sites makes the
 * surfaces agree, which is worth more than leaving half of them to disagree, but it is a
 * behaviour change at the two sites that had raw values and not a no-op.
 */
export function decodePartNumberForWarehouse(pn) {
    if (pn == null || pn === '') {
        return pn;
    }
    var s = String(pn);
    if (!/%[0-9A-Fa-f]{2}/.test(s)) {
        return s;
    }
    try {
        return decodeURIComponent(s);
    } catch (e) {
        return s;
    }
}

/**
 * One request-batch entry, the five-key shape whs/get_inventory_and_pricing reads
 * (Whs::createPiesItems). Missing fields become '' rather than undefined so the POST
 * body is stable across surfaces; the part number is decoded.
 */
export function buildInventoryBatchEntry(fields) {
    return {
        aaia: fields.aaia || '',
        partnumber: decodePartNumberForWarehouse(fields.partnumber) || '',
        gtin: fields.gtin || '',
        linecode: fields.linecode || '',
        stocknumber: fields.stocknumber || '',
    };
}

/**
 * whs/get_inventory_and_pricing stamps each result with the batch position it came
 * from (`_index`, Whs.php). Reading that back - not counting arrival order - is what
 * keeps an answer tied to its own part whenever the batch was shorter than the row
 * collection that built it or the server reordered.
 */
export function indexItemsByBatchIndex(data) {
    var byBatchIndex = {};
    for (var key in data) {
        if (!Object.prototype.hasOwnProperty.call(data, key)) {
            continue;
        }
        var item = data[key];
        if (item == null || typeof item !== 'object') {
            continue;
        }
        var batchIndex = parseInt(item._index, 10);
        if (isNaN(batchIndex)) {
            batchIndex = parseInt(key, 10);
        }
        if (isNaN(batchIndex)) {
            continue;
        }
        byBatchIndex[batchIndex] = item;
    }
    return byBatchIndex;
}

/** The brand-settings keys the derivation reads, with the all-off defaults. */
export function defaultBrandSettings() {
    return {
        Backorder: '0',
        Dropship: '0',
        // Whether the brand will dropship a part IT has none of either. Read by the
        // zero-total rung in deriveStockDisplay() to pick between "Dropship" and
        // "Place on Backorder".
        DropshipZeroInventory: '0',
        IncludeAAMGroupInventory: '0',
        IncludeManufacturerInventory: '0',
        LeadTimeDays: '',
        DropshipFlatRateShippingCost: '',
    };
}

/**
 * Coerce one ajaxGetBrand echo (or embedded brand payload) into the shape above.
 * catalog/ajaxGetBrand echoes null for an unknown code, so a missing answer falls
 * back to each key's own default rather than to a flag string.
 */
export function brandSettingsFromResponse(data) {
    var defaults = defaultBrandSettings();
    var settings = {};

    for (var key in defaults) {
        var value =
            data && Object.prototype.hasOwnProperty.call(data, key)
                ? data[key]
                : defaults[key];
        settings[key] = value != null ? String(value) : defaults[key];
    }

    return settings;
}

/**
 * Whether a zero-stock part can still be placed. The SAME test the submit gate uses
 * (`backordering == false || Backorder == '0'`), so display and submittability cannot
 * disagree: a row that ends up submittable always carries the backorder wording, and a
 * row that reads Out of Stock is never submittable. #686.
 */
export function isBackorderable(backordering, brandSettings) {
    return !(backordering == false || brandSettings['Backorder'] == '0');
}

/**
 * Sum one response item's inventory the same way for every surface.
 *
 * Warehouse stock is the sum of warehouse_inventory_by_location. Additional stock -
 * AAM group and manufacturer inventory - counts only when the site allows backordering
 * AND the brand opts into including it, and prefers the per-location lists over the
 * scalar fallbacks, skipping empty locations.
 *
 * Returns fresh objects; the response item is never mutated.
 */
export function computeInventoryTotals(item, brandSettings, backordering) {
    var warehouseInventory = 0;
    var additionalInventory = 0;
    var warehouseByLocation = {};
    var locations = item.warehouse_inventory_by_location;

    if (locations) {
        for (var whKey in locations) {
            if (!Object.prototype.hasOwnProperty.call(locations, whKey)) {
                continue;
            }
            var whValue = parseInt(locations[whKey], 10) || 0;
            warehouseInventory += whValue;
            warehouseByLocation[whKey] = locations[whKey];
        }
    }

    if (
        backordering &&
        brandSettings['Backorder'] == '1' &&
        (brandSettings['IncludeManufacturerInventory'] == '1' ||
            brandSettings['IncludeAAMGroupInventory'] == '1')
    ) {
        if (
            Object.prototype.hasOwnProperty.call(
                item,
                'aam_inventory_locations'
            ) &&
            item.aam_inventory_locations
        ) {
            for (var aamKey in item.aam_inventory_locations) {
                if (
                    !Object.prototype.hasOwnProperty.call(
                        item.aam_inventory_locations,
                        aamKey
                    )
                ) {
                    continue;
                }
                var loc = item.aam_inventory_locations[aamKey];
                if (parseInt(loc.inventory, 10) === 0) {
                    continue;
                }
                additionalInventory += parseInt(loc.inventory, 10) || 0;
            }
        } else if (parseInt(item.aam_inventory, 10) > 0) {
            additionalInventory += parseInt(item.aam_inventory, 10);
            warehouseByLocation['AAM Group Inventory'] = item.aam_inventory;
        }

        if (
            Object.prototype.hasOwnProperty.call(
                item,
                'man_inventory_locations'
            ) &&
            item.man_inventory_locations
        ) {
            for (var manKey in item.man_inventory_locations) {
                if (
                    !Object.prototype.hasOwnProperty.call(
                        item.man_inventory_locations,
                        manKey
                    )
                ) {
                    continue;
                }
                var mloc = item.man_inventory_locations[manKey];
                if (parseInt(mloc.inventory, 10) === 0) {
                    continue;
                }
                additionalInventory += parseInt(mloc.inventory, 10) || 0;
            }
        } else if (parseInt(item.man_inventory, 10) > 0) {
            additionalInventory += parseInt(item.man_inventory, 10);
            warehouseByLocation['Manufacturer Inventory'] =
                item.man_inventory;
        }
    }

    return {
        warehouseInventory: warehouseInventory,
        additionalInventory: additionalInventory,
        totalInventory: warehouseInventory + additionalInventory,
        warehouseByLocation: warehouseByLocation,
    };
}

function capCount(count, cap) {
    return cap > 0 && count > cap ? cap + '+' : String(count);
}

function leadTimeParts(brandSettings) {
    var cost = brandSettings['DropshipFlatRateShippingCost']
        ? String(brandSettings['DropshipFlatRateShippingCost'])
        : '';

    // The COMPOSED suffix needs the lead time - " (, $12.00)" is not a lead time - but
    // the flat rate is a fact about the brand on its own, so it is returned either way.
    // partials/product-browse.js renders the two as separate spans and showed the cost
    // span whether or not LeadTimeDays was configured; gating `cost` on the days would
    // have silently dropped it there for a brand that sets a flat rate and no lead time.
    if (!brandSettings['LeadTimeDays']) {
        return { days: '', cost: cost, suffix: '' };
    }
    var suffix = ' (' + brandSettings['LeadTimeDays'];
    if (cost) {
        suffix += ', ' + cost;
    }
    return {
        days: String(brandSettings['LeadTimeDays']),
        cost: cost,
        suffix: suffix + ')',
    };
}

/** The wordings deriveStockDisplay() speaks. All come from the site's own settings. */
export function defaultWordings() {
    return {
        available: 'Available',
        outOfStock: 'Out of Stock',
        unavailable: 'Unavailable',
        backorder: 'Place on Backorder',
        dropship: 'Manufacturer Drop-Ship',
        inStock: 'In Stock',
        dropshipAvailable: 'Dropship Available',
    };
}

/**
 * The stock-state ladder, once.
 *
 * Rungs, in order:
 *   1. the part is not orderable                  -> unavailable
 *   2. stock somewhere, numbers hidden            -> available (no figures)
 *   3. no warehouse stock but AAM/man stock,
 *      and the site orders those on backorder     -> dropship / backorder wording
 *   4. stock somewhere                            -> labelled figure(s)
 *   5. nothing anywhere, brand backorderable      -> dropship / backorder wording
 *   6. nothing anywhere                           -> out of stock
 *
 * DECISION (#301, converging four copies):
 * - Rung 3 requires backordering to be ON. With it off, extra AAM/man stock reads as
 *   an ordinary count at rung 4 - the modal/checkout behaviour, which is also the
 *   behaviour the submit gate applies. Browse used to say "Dropship" regardless.
 * - When both figures exist they are both returned, each labelled and capped on its
 *   own (#687): stockedCount under wordings.inStock, additionalCount under
 *   wordings.dropshipAvailable. Never an undifferentiated sum.
 * - Browse's manufacturer-only ">10 shows as 10+" display cap is gone;
 *   stock_display_cap is the one cap everywhere.
 * - The `'default'` string sentinel branch is gone; the endpoint writes integers.
 *
 * `noStock` is the surface's show_stock flag (browse card class, modal table class,
 * checkout page config - different transports, one meaning). `cap` is
 * stock_display_cap, 0 when unset.
 *
 * Returns a view model:
 *   status: 'unavailable' | 'available' | 'count' | 'dropship' | 'backorder' | 'out-of-stock'
 *   message: the primary wording for the status
 *   leadTimeSuffix: ' (days, cost)' for the dropship statuses, '' otherwise
 *   stockedCount / additionalCount: capped display strings, set only when both
 *     pools hold stock; singleCount: the one capped figure otherwise
 *   inStock: whether any stock exists (the modal's check icon follows it - #686
 *     keeps the icon off the zero-stock backorder wording)
 *   leadTimeDays / flatRateCost: raw brand values for surfaces that render them as
 *     their own spans; leadTimeSuffix is the composed ' (days, cost)'
 */
export function deriveStockDisplay(state) {
    var brandSettings = state.brandSettings;
    var wordings = state.wordings;
    var cap = state.cap || 0;

    if (state.item.orderable === false) {
        return {
            status: 'unavailable',
            message: wordings.unavailable,
            inStock: false,
            leadTimeDays: '',
            flatRateCost: '',
            leadTimeSuffix: '',
        };
    }

    var totals =
        state.totals ||
        computeInventoryTotals(
            state.item,
            brandSettings,
            state.backordering
        );
    var warehouseInventory = totals.warehouseInventory;
    var additionalInventory = totals.additionalInventory;
    var totalInventory = totals.totalInventory;

    if (totalInventory > 0) {
        if (state.noStock) {
            // Hiding stock NUMBERS is not hiding availability.
            return {
                status: 'available',
                message: wordings.available,
                inStock: true,
                leadTimeDays: '',
                flatRateCost: '',
                leadTimeSuffix: '',
            };
        }

        if (
            warehouseInventory === 0 &&
            additionalInventory > 0 &&
            state.backordering &&
            brandSettings['Backorder'] == '1'
        ) {
            // Stock exists only outside the warehouse: say how it ships. A brand
            // that dropships out of its own stock does not necessarily dropship
            // this part from AAM/man stock, so DropshipZeroInventory does not
            // apply at this rung.
            var dropshipHere = brandSettings['Dropship'] != '0';
            var lead = leadTimeParts(brandSettings);
            return {
                status: dropshipHere ? 'dropship' : 'backorder',
                message: dropshipHere ? wordings.dropship : wordings.backorder,
                inStock: true,
                leadTimeDays: dropshipHere ? lead.days : '',
                flatRateCost: dropshipHere ? lead.cost : '',
                leadTimeSuffix: dropshipHere ? lead.suffix : '',
            };
        }

        var bothFigures =
            warehouseInventory > 0 && additionalInventory > 0;

        var view = {
            status: 'count',
            message: wordings.available,
            inStock: true,
            leadTimeDays: '',
            flatRateCost: '',
            leadTimeSuffix: '',
            bothFigures: bothFigures,
        };

        if (bothFigures) {
            // Two labelled figures (#687), each capped on its own.
            view.stockedCount = capCount(warehouseInventory, cap);
            view.additionalCount = capCount(additionalInventory, cap);
        } else {
            // Single-figure form keeps today's wording ("N Available"), fed by
            // whichever pool actually holds the stock.
            view.singleCount = capCount(
                warehouseInventory > 0 ? warehouseInventory : additionalInventory,
                cap
            );
        }

        return view;
    }

    if (isBackorderable(state.backordering, brandSettings)) {
        // Zero everywhere: now DropshipZeroInventory decides, because the question
        // is whether the brand will dropship a part IT has none of either.
        var zeroDropship = !(
            brandSettings['Dropship'] == '0' ||
            brandSettings['DropshipZeroInventory'] == '0'
        );
        var zeroLead = leadTimeParts(brandSettings);
        return {
            status: zeroDropship ? 'dropship' : 'backorder',
            message: zeroDropship
                ? wordings.dropship
                : wordings.backorder,
            inStock: false,
            leadTimeDays: zeroDropship ? zeroLead.days : '',
            flatRateCost: zeroDropship ? zeroLead.cost : '',
            leadTimeSuffix: zeroDropship ? zeroLead.suffix : '',
        };
    }

    return {
        status: 'out-of-stock',
        message: wordings.outOfStock,
        inStock: false,
        leadTimeDays: '',
        flatRateCost: '',
        leadTimeSuffix: '',
    };
}
